/**
 * Iter 34 — Schedule Period (School Year) management.
 *
 * Solo-first: a provider owns their own school years. Connected mode
 * will later layer school-owned periods on top via the same surface
 * (school_id is already on the model, just unused in solo).
 *
 * Features
 *   • List all periods (active highlighted, archived collapsed)
 *   • New Period
 *   • Edit (name + dates) inline dialog
 *   • Activate / Archive / Unarchive / Delete (empty-only)
 *   • Roll over to a new school year — wizard with student-assignment
 *     keep/strip choice, default = strip (per user spec)
 */
import { useEffect, useMemo, useState } from "react";
import { Calendar, Plus, ArrowsClockwise, PencilSimple, Trash, Archive, ArrowCounterClockwise, CheckCircle, Warning } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// FP3-051 — Cleaner date entry pattern (MM/DD/YYYY input + calendar
// popover with month/year dropdowns) matching the Admin Student
// profile date fields. Replaces the platform `<input type="date">`
// which was hard to use for year changes.
import DateField from "@/components/admin/student/DateField";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import api from "@/lib/api";

function formatDateRange(start, end) {
  if (!start || !end) return "";
  const fmt = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${fmt(start)} – ${fmt(end)}`;
}

function defaultRolloverDates(srcStart, srcEnd) {
  // Bump both by one year preserving month/day. Falls back to today if
  // the source dates are missing.
  const bump = (iso) => {
    if (!iso) return iso;
    const [y, m, d] = iso.split("-");
    return `${parseInt(y, 10) + 1}-${m}-${d}`;
  };
  return { newStart: bump(srcStart), newEnd: bump(srcEnd) };
}

function defaultRolloverName(srcName, srcStart) {
  // "2025–2026 School Year" → "2026–2027 School Year". If the name has
  // no year hint, we default to current year +1 / +2 for clarity.
  const m = (srcName || "").match(/(\d{4})[–\-](\d{4})/);
  if (m) {
    const [, a, b] = m;
    return (srcName || "").replace(`${a}–${b}`, `${parseInt(a, 10) + 1}–${parseInt(b, 10) + 1}`)
                          .replace(`${a}-${b}`, `${parseInt(a, 10) + 1}-${parseInt(b, 10) + 1}`);
  }
  if (srcStart) {
    const yr = parseInt(srcStart.slice(0, 4), 10);
    return `${yr + 1}–${yr + 2} School Year`;
  }
  return "New School Year";
}

export default function SchoolYearTab({ provider, activePeriod, onPeriodsChanged }) {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  const [editing, setEditing] = useState(null); // period or {} for new
  const [editForm, setEditForm] = useState({ name: "", start_date: "", end_date: "" });
  const [saving, setSaving] = useState(false);

  const [rolloverSource, setRolloverSource] = useState(null);
  const [rolloverForm, setRolloverForm] = useState(null);
  const [rolling, setRolling] = useState(false);

  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const refresh = async () => {
    if (!provider?.id) return;
    setLoading(true);
    try {
      const r = await api.listSchedulePeriods({ provider_id: provider.id });
      setPeriods(r.data || []);
    } catch (e) {
      toast.error("Failed to load periods", { description: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.id]);

  const visible = useMemo(() => {
    return periods
      .filter((p) => showArchived || !p.archived)
      .sort((a, b) => {
        // Active first, then by start_date desc
        if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
        return (b.start_date || "").localeCompare(a.start_date || "");
      });
  }, [periods, showArchived]);

  const archivedCount = periods.filter((p) => p.archived).length;

  // ── Edit / create ────────────────────────────────────────────────────────
  const openEdit = (period) => {
    if (period) {
      setEditing(period);
      setEditForm({ name: period.name || "", start_date: period.start_date || "", end_date: period.end_date || "" });
    } else {
      // new
      const today = new Date();
      const yr = today.getFullYear();
      setEditing({});
      setEditForm({
        name: `${yr}–${yr + 1} School Year`,
        start_date: `${yr}-08-15`,
        end_date: `${yr + 1}-05-30`,
      });
    }
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (editForm.end_date < editForm.start_date) {
      toast.error("End date must be on or after start date");
      return;
    }
    setSaving(true);
    try {
      if (editing && editing.id) {
        await api.updateSchedulePeriod(editing.id, {
          name: editForm.name.trim(),
          start_date: editForm.start_date,
          end_date: editForm.end_date,
        });
        toast.success("Period updated");
      } else {
        await api.createSchedulePeriod({
          provider_id: provider.id,
          name: editForm.name.trim(),
          start_date: editForm.start_date,
          end_date: editForm.end_date,
          is_active: false,
        });
        toast.success("Period created");
      }
      setEditing(null);
      await refresh();
      onPeriodsChanged?.();
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  // ── Activate / archive / unarchive ───────────────────────────────────────
  const activate = async (id) => {
    try {
      await api.activateSchedulePeriod(id);
      toast.success("Period activated");
      await refresh();
      onPeriodsChanged?.();
    } catch (e) {
      toast.error("Activate failed", { description: e.response?.data?.detail || e.message });
    }
  };

  const setArchived = async (id, archived) => {
    try {
      await api.updateSchedulePeriod(id, { archived });
      toast.success(archived ? "Period archived" : "Period unarchived");
      await refresh();
      onPeriodsChanged?.();
    } catch (e) {
      toast.error("Failed", { description: e.response?.data?.detail || e.message });
    }
  };

  // ── Delete (FP3-079D — allowed for ANY period, typed confirm) ───────────
  const openDelete = async (p) => {
    setDeleteConfirmText("");
    // Fetch usage so the modal can show exactly what gets removed.
    let usage = { block_count: 0, wizard_archive_count: 0 };
    try {
      const r = await api.getSchedulePeriod(p.id);
      usage = r.data?.usage || usage;
    } catch (e) { /* non-blocking */ }
    setDeleteCandidate({ ...p, _usage: usage });
  };

  const performDelete = async (id) => {
    setDeleting(true);
    try {
      const r = await api.deleteSchedulePeriod(id, { confirm: "DELETE" });
      const removed = r.data?.removed || {};
      toast.success("Schedule period deleted", {
        description:
          `${removed.recurring_blocks || 0} recurring block(s) and ` +
          `${removed.recurring_block_archives || 0} wizard archive(s) removed. ` +
          "Students, goals, and history untouched.",
      });
      setDeleteCandidate(null);
      await refresh();
      onPeriodsChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error("Delete failed", { description: detail });
    } finally {
      setDeleting(false);
    }
  };

  // ── Rollover ────────────────────────────────────────────────────────────
  const openRollover = async (src) => {
    setRolloverSource(src);
    const { newStart, newEnd } = defaultRolloverDates(src.start_date, src.end_date);
    // Fetch usage so we can show "X blocks will be copied"
    let usage = { block_count: 0, wizard_archive_count: 0 };
    try {
      const r = await api.getSchedulePeriod(src.id);
      usage = r.data?.usage || usage;
    } catch (e) { /* non-blocking */ }
    setRolloverForm({
      new_name: defaultRolloverName(src.name, src.start_date),
      new_start_date: newStart,
      new_end_date: newEnd,
      copy_blocks: true,
      keep_student_assignments: false, // default per user spec
      activate_new: true,
      _usage: usage,
    });
  };

  const performRollover = async () => {
    if (!rolloverSource || !rolloverForm) return;
    if (!rolloverForm.new_name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (rolloverForm.new_end_date < rolloverForm.new_start_date) {
      toast.error("End date must be on or after start date");
      return;
    }
    setRolling(true);
    try {
      const { _usage, ...payload } = rolloverForm;
      const r = await api.rolloverSchedulePeriod(rolloverSource.id, payload);
      const summary = r.data?.summary || {};
      const desc = [
        `${summary.blocks_copied || 0} block${(summary.blocks_copied || 0) === 1 ? "" : "s"} copied`,
        rolloverForm.keep_student_assignments
          ? `${summary.students_kept || 0} student assignment${(summary.students_kept || 0) === 1 ? "" : "s"} kept`
          : "student assignments cleared",
        rolloverForm.activate_new ? "new period activated" : "new period saved as inactive",
      ].join(" · ");
      toast.success("Rollover complete", { description: desc });
      setRolloverSource(null);
      setRolloverForm(null);
      await refresh();
      onPeriodsChanged?.();
    } catch (e) {
      toast.error("Rollover failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setRolling(false);
    }
  };

  if (!provider) {
    return <div className="p-4 text-sm text-[#6B7280]">Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="school-year-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-[#1F2937] heading-font">School year periods</h3>
          <p className="text-xs text-[#6B7280] mt-0.5">
            One active period drives your calendar, builder, wizard, and health math.
            Roll over to start a new year with the same blocks.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {archivedCount > 0 && (
            <button
              data-testid="toggle-archived"
              onClick={() => setShowArchived((v) => !v)}
              className="text-[11px] text-[#1E3F33] hover:underline"
            >
              {showArchived ? "Hide" : "Show"} archived ({archivedCount})
            </button>
          )}
          <Button
            data-testid="new-period-btn"
            onClick={() => openEdit(null)}
            size="sm"
            variant="outline"
            className="border-[#E5E3DB] text-[#1F2937] hover:bg-[#F3F1EC]"
          >
            <Plus size={14} className="mr-1" /> New period
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-[#6B7280] italic py-4">Loading…</p>
      ) : visible.length === 0 ? (
        <Card className="border-[#E5E3DB] border-dashed">
          <CardContent className="py-10 text-center">
            <Calendar size={32} className="text-[#9B9890] mx-auto mb-2" />
            <p className="text-sm text-[#6B7280]">No periods yet.</p>
            <Button onClick={() => openEdit(null)} className="mt-3 bg-[#1E3F33] hover:bg-[#173127] text-white" size="sm">
              <Plus size={14} className="mr-1" /> Create first period
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {visible.map((p) => (
            <PeriodRow
              key={p.id}
              period={p}
              onActivate={() => activate(p.id)}
              onEdit={() => openEdit(p)}
              onArchive={() => setArchived(p.id, true)}
              onUnarchive={() => setArchived(p.id, false)}
              onDelete={() => openDelete(p)}
              onRollover={() => openRollover(p)}
            />
          ))}
        </div>
      )}

      {/* ── Edit / Create dialog ─────────────────────────────────────────── */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="period-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing && editing.id ? "Edit period" : "New period"}</DialogTitle>
            <DialogDescription className="text-[12px]">
              {editing && editing.id
                ? "Rename or move the date range. Use Activate to make it the active period."
                : "Create a new period frame. You can activate it now or later."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Name</Label>
              <Input
                data-testid="period-name-input"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="2026–2027 School Year"
                className="mt-1"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Start date</Label>
                <DateField
                  testid="period-start-input"
                  value={editForm.start_date}
                  onChange={(v) => setEditForm({ ...editForm, start_date: v })}
                  placeholder="MM/DD/YYYY"
                />
              </div>
              <div>
                <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">End date</Label>
                <DateField
                  testid="period-end-input"
                  value={editForm.end_date}
                  onChange={(v) => setEditForm({ ...editForm, end_date: v })}
                  placeholder="MM/DD/YYYY"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              data-testid="period-save-btn"
              onClick={saveEdit}
              disabled={saving}
              className="bg-[#1E3F33] hover:bg-[#173127] text-white"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Rollover wizard ─────────────────────────────────────────────── */}
      <Dialog open={!!rolloverSource} onOpenChange={(o) => !o && (setRolloverSource(null), setRolloverForm(null))}>
        <DialogContent className="max-w-lg" data-testid="rollover-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowsClockwise size={18} weight="duotone" className="text-[#1E3F33]" />
              Roll over to a new school year
            </DialogTitle>
            {rolloverSource && (
              <DialogDescription className="text-[12px]">
                Cloning <span className="font-medium text-[#1F2937]">{rolloverSource.name}</span>
                {rolloverForm?._usage?.block_count > 0
                  ? ` · ${rolloverForm._usage.block_count} block${rolloverForm._usage.block_count === 1 ? "" : "s"} available to copy`
                  : ""}
              </DialogDescription>
            )}
          </DialogHeader>

          {rolloverForm && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">New period name</Label>
                <Input
                  data-testid="rollover-name-input"
                  value={rolloverForm.new_name}
                  onChange={(e) => setRolloverForm({ ...rolloverForm, new_name: e.target.value })}
                  className="mt-1"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Start date</Label>
                  <Input
                    data-testid="rollover-start-input"
                    type="date"
                    value={rolloverForm.new_start_date}
                    onChange={(e) => setRolloverForm({ ...rolloverForm, new_start_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider">End date</Label>
                  <Input
                    data-testid="rollover-end-input"
                    type="date"
                    value={rolloverForm.new_end_date}
                    onChange={(e) => setRolloverForm({ ...rolloverForm, new_end_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <Separator />

              <div className="flex items-center justify-between bg-[#FAFAF7] border border-[#E5E3DB] rounded-md px-3 py-2">
                <div className="text-[12px] text-[#1F2937]">
                  <p className="font-medium">Copy block frames</p>
                  <p className="text-[11px] text-[#6B7280]">Clones every recurring block (service + personal) into the new period.</p>
                </div>
                <Switch
                  data-testid="rollover-copy-blocks"
                  checked={rolloverForm.copy_blocks}
                  onCheckedChange={(v) => setRolloverForm({ ...rolloverForm, copy_blocks: !!v })}
                />
              </div>

              {rolloverForm.copy_blocks && (
                <div>
                  <Label className="text-[11px] text-[#6B7280] uppercase tracking-wider mb-2 block">Student assignments</Label>
                  <RadioGroup
                    value={rolloverForm.keep_student_assignments ? "keep" : "strip"}
                    onValueChange={(v) => setRolloverForm({ ...rolloverForm, keep_student_assignments: v === "keep" })}
                    className="space-y-2"
                  >
                    <label
                      data-testid="rollover-strip-option"
                      className="flex items-start gap-2 border border-[#E5E3DB] rounded-md px-3 py-2 cursor-pointer hover:bg-[#FAFAF7] has-[:checked]:border-[#1E3F33] has-[:checked]:bg-[#E8F0EB]/50"
                    >
                      <RadioGroupItem value="strip" id="strip" className="mt-0.5" />
                      <div className="text-[12px] text-[#1F2937]">
                        <p className="font-medium">Strip student assignments <span className="text-[10px] text-[#1E5A3F] bg-[#E1EAE4] px-1.5 py-0.5 rounded ml-1">recommended</span></p>
                        <p className="text-[11px] text-[#6B7280]">Block frames carry over empty so you can re-wizard for the new roster.</p>
                      </div>
                    </label>
                    <label
                      data-testid="rollover-keep-option"
                      className="flex items-start gap-2 border border-[#E5E3DB] rounded-md px-3 py-2 cursor-pointer hover:bg-[#FAFAF7] has-[:checked]:border-[#1E3F33] has-[:checked]:bg-[#E8F0EB]/50"
                    >
                      <RadioGroupItem value="keep" id="keep" className="mt-0.5" />
                      <div className="text-[12px] text-[#1F2937]">
                        <p className="font-medium">Keep student assignments</p>
                        <p className="text-[11px] text-[#6B7280]">Use when your roster is the same next year (e.g., returning to the same caseload).</p>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              )}

              <div className="flex items-center justify-between bg-[#FAFAF7] border border-[#E5E3DB] rounded-md px-3 py-2">
                <div className="text-[12px] text-[#1F2937]">
                  <p className="font-medium">Make active immediately</p>
                  <p className="text-[11px] text-[#6B7280]">Deactivates the current period. You can switch back later.</p>
                </div>
                <Switch
                  data-testid="rollover-activate-new"
                  checked={rolloverForm.activate_new}
                  onCheckedChange={(v) => setRolloverForm({ ...rolloverForm, activate_new: !!v })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setRolloverSource(null); setRolloverForm(null); }}>Cancel</Button>
            <Button
              data-testid="rollover-confirm-btn"
              onClick={performRollover}
              disabled={rolling || !rolloverForm}
              className="bg-[#1E3F33] hover:bg-[#173127] text-white"
            >
              <ArrowsClockwise size={14} className="mr-1.5" />
              {rolling ? "Rolling over…" : "Roll over"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete confirm (FP3-079D — typed DELETE, any period) ────────── */}
      <Dialog open={!!deleteCandidate} onOpenChange={(o) => !o && setDeleteCandidate(null)}>
        <DialogContent data-testid="delete-period-modal" className="sm:max-w-md bg-white border-[#F4D1D1]">
          <DialogHeader>
            <DialogTitle className="text-[15px] flex items-center gap-2 text-[#7A1F1F]">
              <Warning size={16} weight="duotone" className="text-[#9B2B2B]" />
              Delete this schedule period?
            </DialogTitle>
            <DialogDescription className="text-[12.5px]">
              <span className="font-semibold">{deleteCandidate?.name}</span>{" "}
              ({formatDateRange(deleteCandidate?.start_date, deleteCandidate?.end_date)}).
              This will remove the schedule period and schedule information
              tied to this period, including recurring blocks or calendar
              setup connected to this school year.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div
              data-testid="delete-period-usage"
              className="rounded-md border border-[#F4D1D1] bg-[#FBEFE8] px-3 py-2 text-[11.5px] text-[#7A3A2C] leading-relaxed"
            >
              <span className="font-semibold">Will be removed:</span>{" "}
              {deleteCandidate?._usage?.block_count || 0} recurring block(s) and{" "}
              {deleteCandidate?._usage?.wizard_archive_count || 0} wizard archive(s)
              tied to this period.
              {deleteCandidate?.is_active && (
                <span className="block mt-1 font-semibold">
                  This is your ACTIVE period — after deletion, Schedule
                  returns to the setup state until you create or activate
                  another period.
                </span>
              )}
            </div>
            <p className="text-[11.5px] text-[#4B5563] leading-relaxed">
              This will <span className="font-semibold">not</span> delete
              students, providers, goals, notes, documents, assessments, or
              student history.
            </p>
            <div>
              <label
                htmlFor="delete-period-typed"
                className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
              >
                Type <span className="font-mono text-[#9B2B2B]">DELETE</span> to confirm
                <span className="text-[#A8453A]"> *</span>
              </label>
              <Input
                id="delete-period-typed"
                data-testid="delete-period-typed"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="mt-1 font-mono text-[12.5px]"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteCandidate(null)}
              disabled={deleting}
              data-testid="delete-period-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => deleteCandidate && performDelete(deleteCandidate.id)}
              disabled={deleting || deleteConfirmText.trim().toUpperCase() !== "DELETE"}
              data-testid="delete-period-confirm-btn"
              className="bg-[#9B2B2B] hover:bg-[#7A1F1F] text-white disabled:bg-[#9B2B2B]/40 disabled:cursor-not-allowed"
            >
              <Trash size={12} className="mr-1.5" />
              {deleting ? "Deleting…" : "Delete schedule period"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PeriodRow({ period, onActivate, onEdit, onArchive, onUnarchive, onDelete, onRollover }) {
  const isActive = period.is_active && !period.archived;
  return (
    <Card
      data-testid={`period-row-${period.id}`}
      className={`border ${isActive ? "border-[#1E3F33] bg-[#E8F0EB]/30" : period.archived ? "border-[#E5E3DB] bg-[#FAFAF7] opacity-75" : "border-[#E5E3DB]"}`}
    >
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-semibold text-[#1F2937]">{period.name}</h4>
              {isActive && (
                <Badge className="bg-[#1E3F33] text-white hover:bg-[#1E3F33] text-[10px]" data-testid={`active-badge-${period.id}`}>
                  <CheckCircle size={10} weight="fill" className="mr-1" />
                  Active
                </Badge>
              )}
              {period.archived && (
                <Badge variant="outline" className="text-[10px] border-[#9B9890] text-[#6B7280]">Archived</Badge>
              )}
              {period.rolled_over_from_id && (
                <Badge variant="outline" className="text-[10px] border-[#E5E3DB] text-[#9B9890]">
                  rolled from previous
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-[#6B7280] mt-0.5 tabular-nums">
              {formatDateRange(period.start_date, period.end_date)}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {isActive && (
              <Button
                data-testid={`rollover-btn-${period.id}`}
                onClick={onRollover}
                size="sm"
                className="h-7 text-[11px] bg-[#9C6B2E] hover:bg-[#86571f] text-white"
              >
                <ArrowsClockwise size={12} className="mr-1" /> Roll over
              </Button>
            )}
            {!isActive && !period.archived && (
              <Button
                data-testid={`activate-btn-${period.id}`}
                onClick={onActivate}
                size="sm"
                variant="outline"
                className="h-7 text-[11px] border-[#1E3F33]/30 text-[#1E3F33] hover:bg-[#E8F0EB]"
              >
                <CheckCircle size={12} className="mr-1" /> Activate
              </Button>
            )}
            <Button
              data-testid={`edit-btn-${period.id}`}
              onClick={onEdit}
              size="sm"
              variant="outline"
              className="h-7 text-[11px] border-[#E5E3DB]"
            >
              <PencilSimple size={12} className="mr-1" /> Edit
            </Button>
            {!period.archived ? (
              <Button
                data-testid={`archive-btn-${period.id}`}
                onClick={onArchive}
                size="sm"
                variant="outline"
                disabled={isActive}
                title={isActive ? "Activate a different period before archiving" : "Archive"}
                className="h-7 text-[11px] border-[#E5E3DB] text-[#6B7280]"
              >
                <Archive size={12} className="mr-1" /> Archive
              </Button>
            ) : (
              <Button
                data-testid={`unarchive-btn-${period.id}`}
                onClick={onUnarchive}
                size="sm"
                variant="outline"
                className="h-7 text-[11px] border-[#E5E3DB]"
              >
                <ArrowCounterClockwise size={12} className="mr-1" /> Unarchive
              </Button>
            )}
            <Button
              data-testid={`delete-btn-${period.id}`}
              onClick={onDelete}
              size="sm"
              variant="outline"
              title="Delete schedule period"
              className="h-7 text-[11px] border-[#B85C5C]/30 text-[#B85C5C] hover:bg-[#FBEFEC]"
            >
              <Trash size={12} />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
